import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ListTree, Plus, TriangleAlert } from "lucide-react"

import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { useRbac } from "@/lib/auth"
import { cn } from "@/lib/utils"
import { onWriteError } from "@/lib/writeErrors"

import {
  crearCuentaGerencial,
  editarCuentaGerencial,
  fetchPlanGerencial,
} from "@/routes/masters/api"
import {
  avisoDeCambioDeSeccion,
  avisoDeDesactivar,
  borradorDe,
  cambiosDe,
  cuentaNuevaAApi,
  hayCambios,
  validarCuentaNueva,
  CUENTA_NUEVA_VACIA,
  QUE_HACE,
  type BorradorCuenta,
  type CuentaGerencial,
  type CuentaNueva,
  type PlanGerencial,
  type TipoEstado,
} from "@/routes/masters/plan"
import { t as traducir } from "@/lib/i18n"

/**
 * EL P&L DEL CLIENTE. Crear, renombrar, reordenar, declarar si suma al EBITDA
 * y desactivar.
 *
 * POR QUE ESTA PANTALLA ES DEL CLIENTE Y NO NUESTRA. `account_local` es
 * normativo —el PCGE lo aprueba el Consejo Normativo y no se negocia—;
 * `account_group` es lo contrario: no lo exige ningun fisco. Se disena para
 * contestar las preguntas del negocio, y esas preguntas cambian con el negocio.
 * Una agencia de servicios tiene costo de ventas; una licenciadora de software
 * puede no tenerlo, y entonces el «margen bruto» que el sistema calcula no
 * significa nada para ella. Era la Fase 2 que nunca se construyo, asi que hasta
 * hoy el cliente se quedaba con el plan de la semilla.
 *
 * LOS DOS AVISOS SE DAN ANTES, NO DESPUES. El backend los devuelve DESPUES de
 * escribir, y para entonces la decision ya se tomo:
 *
 * * **Desactivar con mapeos vivos.** El listado trae `mapeos_vigentes`, asi que
 *   el numero de reglas que van a quedar colgando se dice mientras todavia se
 *   puede no hacerlo. Y desactivar NO es borrar: una cuenta usada en un periodo
 *   cerrado tiene que seguir existiendo o los estados de ese periodo dejan de
 *   poder reconstruirse.
 * * **Cambiar de tipo.** Mover una cuenta de «gasto» a «costo» no es una
 *   etiqueta: el importe cambia de seccion en el P&L de todos los periodos
 *   abiertos, y el margen bruto con el.
 *
 * El CODIGO no se edita, y no es un olvido: es la clave contra la que apunta
 * `account_map`. Cambiarlo dejaria los mapeos resolviendo a otra cosa. Para
 * cambiar de codigo se abre una cuenta nueva y se desactiva la vieja, que
 * ademas deja el rastro de que hubo un cambio.
 */
export function PlanGerencialPage() {
  const [inactivas, setInactivas] = React.useState(false)
  const q = useQuery({
    queryKey: ["plan-gerencial", inactivas],
    queryFn: () => fetchPlanGerencial(inactivas),
  })
  const { can } = useRbac()
  const puedeEditar = can("edit")
  const [alta, setAlta] = React.useState(false)

  return (
    <div className="space-y-3">
      <DataState
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && !q.error && (q.data?.cuentas.length ?? 0) === 0}
        emptyMessage={traducir("Este cliente no tiene plan de P&L.")}
        emptyHint={traducir("Sin él no hay estado de resultados de gestión: los comprobantes se clasifican contra el plan normativo y no hay dónde sumarlos. Abre las cuentas aquí.")}
        onRetry={() => void q.refetch()}
        columns={[10, 26, 20, 12, 16, 16]}
      >
        {q.data ? (
          <Plan
            plan={q.data}
            puedeEditar={puedeEditar}
            inactivas={inactivas}
            onInactivas={setInactivas}
            alta={alta}
            onAlta={setAlta}
          />
        ) : null}
      </DataState>
    </div>
  )
}

function Plan({
  plan,
  puedeEditar,
  inactivas,
  onInactivas,
  alta,
  onAlta,
}: {
  plan: PlanGerencial
  puedeEditar: boolean
  inactivas: boolean
  onInactivas: (v: boolean) => void
  alta: boolean
  onAlta: (v: boolean) => void
}) {
  const secciones = React.useMemo(
    () => Object.fromEntries(plan.tipos.map((t) => [t.codigo, t.seccion])),
    [plan.tipos],
  )
  const desactivadas = plan.cuentas.filter((c) => !c.activo).length

  return (
    <>
      {/*
        LA PREGUNTA DE NEGOCIO, contestada con el plan delante. Una empresa sin
        cuentas de COSTO no tiene margen bruto que calcular, y el dashboard se
        lo va a mostrar igual —en cero— si nadie lo dice.
      */}
      {!plan.tiene_costo_de_ventas ? (
        <p className="flex items-start gap-1.5 rounded-md border border-warning/30 bg-warning-soft/40 px-2.5 py-2 text-2xs leading-4 text-foreground">
          <TriangleAlert className="mt-px h-3 w-3 shrink-0 text-warning" />
          <span>
            <strong className="font-medium">{traducir("Este plan no tiene ninguna cuenta de costo")}</strong>{traducir(", así que no hay margen bruto que calcular. El dashboard lo va a mostrar igual, en cero, y un cero se lee como «no vendió con margen» en vez de como «esta empresa no separa costo de ventas». Si sí lo separa, abre las cuentas con tipo «costo».")}</span>
        </p>
      ) : null}

      <WorkPanel
        title={traducir("Plan del P&L")}
        meta={
          <>
            <Badge variant="outline">{plan.cuentas.filter((c) => c.activo).length} activas</Badge>
            {inactivas && desactivadas > 0 ? (
              <Badge variant="outline">{desactivadas} desactivadas</Badge>
            ) : null}
          </>
        }
        actions={
          <>
            <label className="flex items-center gap-1.5 text-2xs text-muted-foreground">
              <Checkbox
                checked={inactivas}
                onCheckedChange={(v) => onInactivas(v === true)}
              />
              {traducir("Ver también las desactivadas")}</label>
            {puedeEditar ? (
              <Button size="xs" variant="outline" onClick={() => onAlta(!alta)}>
                <Plus />
                {alta ? "Cancelar" : "Nueva cuenta"}
              </Button>
            ) : null}
          </>
        }
        footnote={plan.nota}
      >
        {alta ? (
          <AltaDeCuenta plan={plan} onListo={() => onAlta(false)} />
        ) : null}

        <Table className="table-dense">
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">{traducir("Código")}</TableHead>
              <TableHead className="w-[18rem]">{traducir("Nombre")}</TableHead>
              <TableHead className="w-[16rem]">{traducir("Dónde entra")}</TableHead>
              <TableHead className="w-24">{traducir("EBITDA")}</TableHead>
              <TableHead className="w-20 text-right">{traducir("Orden")}</TableHead>
              <TableHead>{traducir("En uso")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plan.cuentas.map((c) => (
              <Fila key={c.id} cuenta={c} secciones={secciones} plan={plan} puedeEditar={puedeEditar} />
            ))}
          </TableBody>
        </Table>
      </WorkPanel>
    </>
  )
}

function Fila({
  cuenta,
  secciones,
  plan,
  puedeEditar,
}: {
  cuenta: CuentaGerencial
  secciones: Record<string, string>
  plan: PlanGerencial
  puedeEditar: boolean
}) {
  const [abierto, setAbierto] = React.useState(false)

  return (
    <>
      <TableRow className={cn(!cuenta.activo && "opacity-60")}>
        <TableCell className="code font-medium">{cuenta.codigo}</TableCell>
        <TableCell className="truncate" title={cuenta.nombre}>
          {cuenta.nombre}
          {!cuenta.activo ? (
            <Badge variant="outline" className="ml-1.5">
              desactivada
            </Badge>
          ) : null}
        </TableCell>
        <TableCell className="text-2xs leading-4 text-muted-foreground">
          {cuenta.seccion}
        </TableCell>
        <TableCell>
          {cuenta.ebitda_efectivo ? (
            <Badge variant={cuenta.ebitda_declarado ? "success-soft" : "outline"} estado>
              {cuenta.ebitda_declarado ? "suma" : "suma (derivado)"}
            </Badge>
          ) : (
            <Badge variant={cuenta.ebitda_declarado ? "warning-soft" : "outline"} estado>
              {cuenta.ebitda_declarado ? "no suma" : "no suma (derivado)"}
            </Badge>
          )}
        </TableCell>
        <TableCell className="num">{cuenta.orden ?? "—"}</TableCell>
        <TableCell>
          <div className="flex flex-wrap items-center gap-2">
            {cuenta.mapeos_vigentes > 0 ? (
              <span className="text-2xs text-muted-foreground">
                {cuenta.mapeos_vigentes}{" "}
                {cuenta.mapeos_vigentes === 1 ? "regla apunta" : "reglas apuntan"}{" "}{traducir("aquí")}</span>
            ) : (
              <span className="text-2xs text-muted-foreground">{traducir("sin mapeos")}</span>
            )}
            {puedeEditar ? (
              <Button size="xs" variant="ghost" onClick={() => setAbierto((v) => !v)}>
                {abierto ? "Cancelar" : "Editar"}
              </Button>
            ) : null}
          </div>
        </TableCell>
      </TableRow>

      {abierto ? (
        <TableRow>
          <TableCell colSpan={6} className="bg-surface-sunken">
            <Editor
              cuenta={cuenta}
              secciones={secciones}
              plan={plan}
              onListo={() => setAbierto(false)}
            />
          </TableCell>
        </TableRow>
      ) : null}
    </>
  )
}

function Editor({
  cuenta,
  secciones,
  plan,
  onListo,
}: {
  cuenta: CuentaGerencial
  secciones: Record<string, string>
  plan: PlanGerencial
  onListo: () => void
}) {
  const [b, setB] = React.useState<BorradorCuenta>(() => borradorDe(cuenta))
  const [motivo, setMotivo] = React.useState("")
  const { toast } = useToast()
  const qc = useQueryClient()

  const cambios = cambiosDe(cuenta, b)
  const listo = hayCambios(cambios) && motivo.trim().length >= 3

  const avisoSeccion = avisoDeCambioDeSeccion(cuenta, b.tipo_estado, secciones)
  const avisoBaja = !b.activo && cuenta.activo ? avisoDeDesactivar(cuenta) : null

  const guardar = useMutation({
    mutationFn: () => editarCuentaGerencial(cuenta.id, { ...cambios, motivo }),
    onSuccess: (r) => {
      toast({
        title: `${r.codigo}: ${r.actualizado.join(", ")}`,
        description: r.aviso ?? undefined,
      })
      onListo()
      void qc.invalidateQueries({ queryKey: ["plan-gerencial"] })
      // El P&L, los estados y el mapa de cuentas se leen contra esto.
      void qc.invalidateQueries({ queryKey: ["pnl"] })
      void qc.invalidateQueries({ queryKey: ["estados"] })
    },
    onError: onWriteError(`la cuenta ${cuenta.codigo}`, "editar"),
  })

  const set = <K extends keyof BorradorCuenta>(k: K, v: BorradorCuenta[K]) =>
    setB((prev) => ({ ...prev, [k]: v }))

  return (
    <div className="animate-row-in space-y-2.5 px-1 py-2">
      <div className="grid gap-3 md:grid-cols-4">
        <div className="space-y-1 md:col-span-2">
          <Label htmlFor={`nom-${cuenta.id}`}>{traducir("Nombre")}</Label>
          <Input
            id={`nom-${cuenta.id}`}
            value={b.nombre}
            onChange={(e) => set("nombre", e.target.value)}
            className="h-7 text-xs"
          />
          <p className="text-2xs text-muted-foreground">
            {traducir("El código")}{" "}<span className="code">{cuenta.codigo}</span> {" "}{traducir("no se edita: es la clave contra la que apunta el mapa local. Para cambiarlo se abre una cuenta nueva y se desactiva ésta.")}</p>
        </div>

        <div className="space-y-1">
          <Label htmlFor={`tipo-${cuenta.id}`}>{traducir("Tipo")}</Label>
          <select
            id={`tipo-${cuenta.id}`}
            value={b.tipo_estado}
            onChange={(e) => set("tipo_estado", e.target.value as TipoEstado)}
            className={cn(
              "h-7 w-full rounded-md border bg-surface px-2 text-xs",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            {plan.tipos.map((t) => (
              <option key={t.codigo} value={t.codigo}>
                {t.codigo}
              </option>
            ))}
          </select>
          <p className="text-2xs leading-4 text-muted-foreground">{QUE_HACE[b.tipo_estado]}</p>
        </div>

        <div className="space-y-1">
          <Label htmlFor={`orden-${cuenta.id}`}>{traducir("Orden")}</Label>
          <Input
            id={`orden-${cuenta.id}`}
            value={b.orden}
            onChange={(e) => set("orden", e.target.value)}
            inputMode="numeric"
            placeholder={traducir("sin orden")}
            className="num h-7 text-xs"
          />
          <p className="text-2xs text-muted-foreground">
            {traducir("Menor va arriba. Vacío las manda al final, ordenadas por código.")}</p>
        </div>

        <div className="space-y-1">
          <Label htmlFor={`ebitda-${cuenta.id}`}>{traducir("¿Suma al EBITDA?")}</Label>
          <select
            id={`ebitda-${cuenta.id}`}
            value={b.ebitda}
            onChange={(e) => set("ebitda", e.target.value as BorradorCuenta["ebitda"])}
            className={cn(
              "h-7 w-full rounded-md border bg-surface px-2 text-xs",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            <option value="">{traducir("Sin declarar — se deriva del tipo")}</option>
            <option value="si">{traducir("Sí, suma")}</option>
            <option value="no">{traducir("No suma")}</option>
          </select>
          <p className="text-2xs leading-4 text-muted-foreground">
            {traducir("«Sin declarar» no es «no suma»: se deriva del tipo, que es lo que el sistema hacía antes de que se pudiera declarar. Con un plan propio, derivar del código deja de valer.")}</p>
        </div>

        <div className="space-y-1">
          <Label htmlFor={`padre-${cuenta.id}`}>{traducir("Cuenta padre")}</Label>
          <Input
            id={`padre-${cuenta.id}`}
            value={b.padre_codigo}
            onChange={(e) => set("padre_codigo", e.target.value)}
            placeholder={traducir("sin padre")}
            className="code h-7 text-xs"
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor={`activo-${cuenta.id}`}>{traducir("Estado")}</Label>
          <label className="flex h-7 items-center gap-1.5 text-xs">
            <Checkbox
              id={`activo-${cuenta.id}`}
              checked={b.activo}
              onCheckedChange={(v) => set("activo", v === true)}
            />
            {traducir("Activa")}</label>
          <p className="text-2xs leading-4 text-muted-foreground">
            {traducir("Desactivar no borra: deja de ofrecerse para clasificar y los asientos viejos siguen resolviendo contra ella.")}</p>
        </div>
      </div>

      {avisoSeccion ? <Aviso texto={avisoSeccion} /> : null}
      {avisoBaja ? <Aviso texto={avisoBaja} /> : null}

      <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
        <div className="space-y-1">
          <Label htmlFor={`mot-${cuenta.id}`}>{traducir("Motivo *")}</Label>
          <Textarea
            id={`mot-${cuenta.id}`}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={2}
            className="text-xs"
            placeholder={traducir("Por qué. Queda en la pista de auditoría con tu nombre y la fecha.")}
          />
        </div>
        <Button size="sm" disabled={!listo || guardar.isPending} onClick={() => guardar.mutate()}>
          <ListTree />
          {guardar.isPending ? "Guardando…" : "Guardar"}
        </Button>
      </div>

      {!hayCambios(cambios) ? (
        <p className="text-2xs text-muted-foreground">{traducir("Todavía no cambiaste nada.")}</p>
      ) : null}
    </div>
  )
}

function Aviso({ texto }: { texto: string }) {
  return (
    <p className="flex items-start gap-1.5 rounded-md border border-warning/30 bg-warning-soft/40 px-2 py-1.5 text-2xs leading-4 text-foreground">
      <TriangleAlert className="mt-px h-3 w-3 shrink-0 text-warning" />
      <span>{texto}</span>
    </p>
  )
}

function AltaDeCuenta({ plan, onListo }: { plan: PlanGerencial; onListo: () => void }) {
  const [n, setN] = React.useState<CuentaNueva>(CUENTA_NUEVA_VACIA)
  const { toast } = useToast()
  const qc = useQueryClient()

  const error = validarCuentaNueva(n, plan.cuentas)

  const crear = useMutation({
    mutationFn: () => crearCuentaGerencial(cuentaNuevaAApi(n)),
    onSuccess: (r) => {
      toast({ title: `${r.codigo} creada`, description: r.seccion })
      setN(CUENTA_NUEVA_VACIA)
      void qc.invalidateQueries({ queryKey: ["plan-gerencial"] })
      void qc.invalidateQueries({ queryKey: ["pnl"] })
      onListo()
    },
    onError: onWriteError("la cuenta del plan", "crear"),
  })

  const set = <K extends keyof CuentaNueva>(k: K, v: CuentaNueva[K]) =>
    setN((prev) => ({ ...prev, [k]: v }))

  return (
    <div className="space-y-2 border-b bg-surface-sunken/40 p-2.5">
      <div className="grid gap-2 md:grid-cols-4">
        <div className="space-y-1">
          <Label htmlFor="nueva-codigo">{traducir("Código *")}</Label>
          <Input
            id="nueva-codigo"
            value={n.codigo}
            onChange={(e) => set("codigo", e.target.value)}
            placeholder="6500"
            className="code h-7 text-xs"
          />
        </div>
        <div className="space-y-1 md:col-span-2">
          <Label htmlFor="nueva-nombre">{traducir("Nombre *")}</Label>
          <Input
            id="nueva-nombre"
            value={n.nombre}
            onChange={(e) => set("nombre", e.target.value)}
            placeholder={traducir("Comisiones de plataforma")}
            className="h-7 text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="nueva-tipo">{traducir("Tipo *")}</Label>
          <select
            id="nueva-tipo"
            value={n.tipo_estado}
            onChange={(e) => set("tipo_estado", e.target.value as TipoEstado)}
            className={cn(
              "h-7 w-full rounded-md border bg-surface px-2 text-xs",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            <option value="">{traducir("Elige el tipo…")}</option>
            {plan.tipos.map((t) => (
              <option key={t.codigo} value={t.codigo}>
                {t.codigo} · {t.seccion}
              </option>
            ))}
          </select>
        </div>
      </div>

      {n.tipo_estado ? (
        <p className="text-2xs leading-4 text-muted-foreground">
          {QUE_HACE[n.tipo_estado as TipoEstado]}
        </p>
      ) : null}

      <div className="grid gap-2 md:grid-cols-4">
        <div className="space-y-1">
          <Label htmlFor="nueva-padre">{traducir("Cuenta padre")}</Label>
          <Input
            id="nueva-padre"
            value={n.padre_codigo}
            onChange={(e) => set("padre_codigo", e.target.value)}
            placeholder="65"
            className="code h-7 text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="nueva-orden">{traducir("Orden")}</Label>
          <Input
            id="nueva-orden"
            value={n.orden}
            onChange={(e) => set("orden", e.target.value)}
            inputMode="numeric"
            className="num h-7 text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="nueva-ebitda">{traducir("¿Suma al EBITDA?")}</Label>
          <select
            id="nueva-ebitda"
            value={n.ebitda}
            onChange={(e) => set("ebitda", e.target.value as CuentaNueva["ebitda"])}
            className={cn(
              "h-7 w-full rounded-md border bg-surface px-2 text-xs",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            <option value="">{traducir("Sin declarar — se deriva del tipo")}</option>
            <option value="si">{traducir("Sí, suma")}</option>
            <option value="no">{traducir("No suma")}</option>
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="nueva-motivo">{traducir("Motivo *")}</Label>
          <Input
            id="nueva-motivo"
            value={n.motivo}
            onChange={(e) => set("motivo", e.target.value)}
            placeholder={traducir("Por qué se abre")}
            className="h-7 text-xs"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={Boolean(error) || crear.isPending} onClick={() => crear.mutate()}>
          <Plus />
          {crear.isPending ? "Creando…" : "Abrir cuenta"}
        </Button>
        {error ? <span className="text-2xs leading-4 text-warning">{error}</span> : null}
      </div>
    </div>
  )
}
